import { useCallback } from "react";
import { useAuth } from "context/AuthContextProvider";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { isManager, isSystemAdmin } from "shared/utils/role";
import {
  AdminCapability,
  isCapabilityRestricted,
} from "shared/utils/capabilities";

// The single source every gated surface reads. Managers and system admins
// resolve immediately without a request; a plain Admin reads the business's
// Pembatasan Admin flags off the cached ["business","settings"] query, which
// MainLayout mounts once so the flags are usually in the cache before any
// page asks. The query has no staleTime, so it also refreshes on window
// focus and after every ["business"] invalidation (i.e. right after a Manager
// saves), which is how a restricted Admin's UI recovers without a reload.
export const useAdminCapabilities = () => {
  const { state } = useAuth();
  const roles = state.user?.roles;
  const isPrivileged = isManager(roles) || isSystemAdmin(roles);
  const { data: settings, isLoading } = useBusinessSettings(!isPrivileged);

  const isRestricted = useCallback(
    (cap: AdminCapability) => isCapabilityRestricted(roles, settings, cap),
    [roles, settings],
  );

  return {
    isPrivileged,
    // Only ever true for a plain Admin whose flags haven't arrived yet.
    isLoading: !isPrivileged && isLoading,
    isRestricted,
    settings,
  };
};
